<template>
    <div
        v-if="isActive || props.test"
        class="relative bg-surface-card overflow-hidden -mx-4 -mt-4"
        :class="!videoVisible || soundDialogOpen ? 'min-h-[88px]' : 'min-h-[40vh]'"
    >
        <BarcodeScanner
            v-if="!soundDialogOpen && videoVisible && !verify.cameraBusy"
            class="absolute inset-0 w-full h-full"
            :on-detected="(c: string) => processCode(c)"
        />

        <!-- Overlay: title up top, code centered, badges in corners -->
        <div class="relative z-10 h-full min-h-[40vh] flex flex-col p-2 pointer-events-none">
            <div class="flex justify-center">
                <h3
                    v-if="!props.test"
                    class="text-center text-sm bg-black/40 rounded px-3 py-1"
                    v-html="activeEventNames"
                />
                <h3 v-else class="text-center text-sm bg-black/40 rounded px-3 py-1">{{ t('menu.test') }}</h3>
            </div>

            <div class="flex-1 flex items-center justify-center">
                <span v-if="readerCode" class="bg-black/40 px-3 py-1 rounded text-xl font-bold tracking-wider">{{
                    readerCode
                }}</span>
            </div>

            <div v-if="!props.test" class="flex items-end justify-between text-sm gap-2">
                <button
                    class="p-2 rounded bg-black/40 hover:bg-black/60 pointer-events-auto"
                    :title="t('scanner.manualTitle')"
                    @click="manualOpen = true"
                >
                    <Keyboard class="w-6 h-6" />
                </button>

                <div class="flex flex-col items-end gap-1">
                    <span v-if="startAt" class="bg-black/40 px-2 py-0.5 rounded">
                        <b>{{ formatDate(startAt, 'D.M.HH:mm') }}</b>
                    </span>
                    <span v-if="settings.showTicketCount && !loading" class="bg-black/40 px-2 py-0.5 rounded">
                        <b>{{ tickets.getUsedTicketsCount }} / {{ tickets.getAllTicketsCount }}</b>
                    </span>
                    <span v-if="loading" class="bg-black/40 px-2 py-0.5 rounded">{{ t('scanner.loading') }}</span>
                </div>
            </div>
        </div>
    </div>

    <div class="space-y-3 mt-3">
        <AppProgressLinear v-if="loading" />
        <MessageList />

        <GroupConfirmDialog
            v-model="groupOpen"
            :ticketset-name="groupName"
            @confirm="onGroupConfirm"
            @refuse="onGroupRefuse"
        />

        <ManualInputDialog v-model="manualOpen" @submit="onManualSubmit" />

        <Teleport to="body">
            <div v-if="soundDialogOpen" class="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4">
                <div class="bg-surface-card rounded p-4 max-w-sm space-y-3">
                    <h3 class="text-center font-semibold">{{ t('scanner.startReader') }}</h3>
                    <p class="text-center text-sm">{{ t('scanner.startReaderDescription') }}</p>
                    <button class="bg-success text-white w-full py-3 rounded" @click="closeSoundDialog">OK</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
    import { useTicketsStore, type Ticket, type TicketsetWithList } from '@/stores/tickets'
    import { useEventsStore } from '@/stores/events'
    import { useMessagesStore } from '@/stores/messages'
    import { useQueueStore, type QueueItem } from '@/stores/queue'
    import { useSettingsStore } from '@/stores/settings'
    import { useNetworkStore } from '@/stores/network'
    import { useAuthStore } from '@/stores/auth'
    import { useVerifyStore } from '@/stores/verify'
    import { useI18n } from 'vue-i18n'
    import BarcodeScanner from '@/components/BarcodeScanner.vue'
    import MessageList from '@/components/MessageList.vue'
    import AppProgressLinear from '@/components/ui/AppProgressLinear.vue'
    import GroupConfirmDialog from '@/components/dialogs/GroupConfirmDialog.vue'
    import ManualInputDialog from '@/components/dialogs/ManualInputDialog.vue'
    import { isAudioContextRunning, initSound, playOk, playAlarm, playCheck } from '@/services/sound'
    import { formatDate } from '@/services/util/date'
    import { sdk } from '@/graphql/client'
    import { startNfcReader, type NfcHandle } from '@/services/nfc/reader'
    import { Keyboard } from 'lucide-vue-next'

    const props = defineProps<{ test?: boolean }>()

    const tickets = useTicketsStore()
    const events = useEventsStore()
    const messages = useMessagesStore()
    const queue = useQueueStore()
    const settings = useSettingsStore()
    const network = useNetworkStore()
    const auth = useAuthStore()
    const verify = useVerifyStore()
    const { t } = useI18n()

    const soundDialogOpen = ref(false)
    const loading = ref(false)
    const videoVisible = ref(settings.camera !== '-')
    const readerCode = ref('')
    const manualOpen = ref(false)
    const groupOpen = ref(false)
    const groupTicket = ref<Ticket | null>(null)
    const groupTicketset = ref<TicketsetWithList | null>(null)
    const groupOnline = ref<boolean>(false)
    const groupName = ref('')
    const tempCode = ref<string[]>([])

    // Po otevření scanneru (typicky deep-link QR, který appku otevřel, je pořád
    // před objektivem) ignoruj detekce po dobu SCAN_SUPPRESS_MS, aby se ten samý
    // QR hned nenačetl jako kontrolovaný lístek.
    const SCAN_SUPPRESS_MS = 5000
    let scanSuppressedUntil = 0
    let clearTicketTimer: number | undefined
    let nfcHandle: NfcHandle | null = null

    const activeEventNames = computed(() => {
        if (events.activeZones.length) {
            const list = events.activeZones
                .map(zid => events.zones.find(z => Number(z.id) === zid)?.name ?? '')
                .filter(Boolean)
                .join(', ')
            return `Zóny: ${list}`
        } else {
            const str = events.activeEvents.map(id => events.events.find(e => e.id === id)?.name ?? '').join(', ')
            return str.length > 32 ? '...více akcí...' : str
        }
    })

    const startAt = computed(() => {
        const id = events.activeEvents[0]
        return id ? (events.events.find(e => e.id === id)?.startAt ?? '') : ''
    })

    const isActive = computed(
        () => Boolean(events.activeCheckpoint) || events.activeEvents.length > 0 || events.activeZones.length > 0
    )

    async function doTicketUse(ticketsetId: number | string, code: string, isOnline: boolean) {
        const payload: QueueItem = {
            ticketsetId: Number(ticketsetId),
            code,
            isOnline: false
        }
        if (events.activeZones.length) payload.zoneIds = events.activeZones

        let data: { ok: boolean; display?: string | null } | null = null

        if (isOnline) {
            try {
                const result = await sdk.TicketUseOnline({
                    ticketsetId: Number(ticketsetId),
                    code,
                    checkpoint: payload.checkpoint,
                    zoneIds: payload.zoneIds
                })
                const r = result.mobile?.ticketUseOnline
                if (r) {
                    data = { ok: Boolean(r.ok), display: r.display ?? null }
                    if (r.ok) {
                        tickets.useCode({
                            ticketsetId: Number(ticketsetId),
                            code,
                            usedByName: auth.user?.name ?? null
                        })
                    }
                }
            } catch {
                network.showNetworkError()
                return
            }
        } else {
            queue.enqueue(payload)
            void queue.flushQueue()
            data = { ok: true }
        }

        if (data && data.ok) {
            let message: string
            const ticketset = tickets.activeTicketsetById(ticketsetId)
            const [ticket] = tickets.getTicketByCode(code)
            const event = tickets.activeEventByTicketsetId(ticketsetId)

            switch (settings.group) {
                case 'ticketset':
                    message = t('scanner.messageSuccessTicketset', { code, ticketset: ticketset?.name ?? '' })
                    break
                case 'price':
                    message = t('scanner.messageSuccessPrice', { code, price: ticket?.price?.name ?? '' })
                    break
                case 'ticketset_price':
                    message = t('scanner.messageSuccessTicketsetPrice', {
                        code,
                        ticketset: ticketset?.name ?? '',
                        price: ticket?.price?.name ?? ''
                    })
                    break
                case 'event':
                    message = t('scanner.messageSuccessEvent', {
                        code,
                        event: event?.name ?? '---',
                        ticketset: ticketset?.name ?? ''
                    })
                    break
                default:
                    message = t('scanner.messageSuccess', { code })
            }

            messages.addMessage({
                type: 'success',
                ticketsetId,
                code,
                message
            })
            if (settings.sound) playOk()
        } else {
            const display = (data && 'display' in data ? data.display : null) ?? ''
            messages.addMessage({
                type: 'used',
                message: t('scanner.messageNotUsable', { code }),
                extra: display
            })
            if (settings.sound) playAlarm()
        }
    }

    function processCode(code: string, noValidate = false) {
        // Suppress scanning for a few seconds after the scanner opens — manual
        // entry / hardware reader (noValidate) bypasses the suppression.
        if (!noValidate && performance.now() < scanSuppressedUntil) return

        let ticket: Ticket | null = null
        let ticketset: TicketsetWithList | null = null
        if (!props.test) {
            ;[ticket, ticketset] = tickets.getTicketByCode(code)
        }

        // anti-noise: require 3 same reads in a row when ticket not found and !noValidate
        if (!props.test && ticket === null) {
            if (tempCode.value.length < 2 && !noValidate) {
                tempCode.value.push(code)
                if (tempCode.value.length > 2) tempCode.value.shift()
                return
            } else if ((code !== tempCode.value[0] || code !== tempCode.value[1]) && !noValidate) {
                tempCode.value.push(code)
                if (tempCode.value.length > 2) tempCode.value.shift()
                return
            }
        }
        tempCode.value = []

        if (props.test) {
            if (code !== tickets.lastCode) {
                messages.addMessage({
                    type: 'success',
                    message: t('scanner.messageRead', { code })
                })
                if (settings.sound) playOk()
                tickets.setLastCode(code)
                window.setTimeout(() => tickets.setLastCode(''), 1000)
            }
            return
        }

        if (code === tickets.lastCode) return

        if (ticket && ticketset) {
            const subUse = (isOnline: boolean) => {
                const fullTicketset = tickets.activeTicketsetById(ticketset!.id)
                if (fullTicketset?.validate) {
                    if (settings.sound) playCheck()
                    groupTicket.value = ticket
                    groupTicketset.value = ticketset
                    groupOnline.value = isOnline
                    groupName.value = fullTicketset.customName || fullTicketset.name
                    groupOpen.value = true
                } else {
                    void doTicketUse(ticketset!.id, code, isOnline)
                }
            }

            if (ticket.isOnline || events.activeCheckpoint) {
                subUse(true)
            } else if (ticket.used) {
                const usedByName = ticket.usedBy?.name?.trim() || 'neznámé'
                const useDate = ticket.usedAt ? new Date(ticket.usedAt).toLocaleString() : ''
                messages.addMessage({
                    type: 'used',
                    message: t('scanner.messageUsed', { code }),
                    extra: t('scanner.messageExtra1', { device: usedByName, date: useDate })
                })
                if (settings.sound) playAlarm()
            } else if (
                queue.sendQueue.find(c => {
                    const shortLen = Math.min(c.code.length, code.length)
                    return (
                        c.code.substring(0, shortLen) === code.substring(0, shortLen) &&
                        String(c.ticketsetId) === String(ticketset!.id)
                    )
                })
            ) {
                messages.addMessage({
                    type: 'used',
                    message: t('scanner.messageUnsend', { code })
                })
                if (settings.sound) playAlarm()
            } else {
                subUse(false)
            }
        } else {
            void sdk
                .MobileInfo({ code })
                .then(result => {
                    const info = (result.mobile?.info ?? '') as string
                    messages.addMessage({
                        type: 'not-found',
                        message: t('scanner.messageNotFound', { code }),
                        extra: info
                    })
                    if (settings.sound) playAlarm()
                })
                .catch(() => {
                    messages.addMessage({
                        type: 'not-found',
                        message: t('scanner.messageNotFound', { code })
                    })
                    if (settings.sound) playAlarm()
                })
        }

        tickets.setLastCode(code)
        if (clearTicketTimer) window.clearTimeout(clearTicketTimer)
        clearTicketTimer = window.setTimeout(() => tickets.setLastCode(''), 5000)
    }

    function onKey(event: KeyboardEvent) {
        if (manualOpen.value || verify.isOpen) return
        if (event.keyCode >= 48 && event.keyCode <= 57) readerCode.value += event.key
        if (event.keyCode >= 65 && event.keyCode <= 90) readerCode.value += event.key
        if (event.keyCode === 8) readerCode.value = readerCode.value.slice(0, -1)
        if (event.keyCode === 13 || event.keyCode === 190) {
            processCode(readerCode.value, true)
            readerCode.value = ''
        }
    }

    function closeSoundDialog() {
        initSound()
        soundDialogOpen.value = false
    }

    function onGroupConfirm() {
        if (groupTicketset.value && groupTicket.value) {
            void doTicketUse(groupTicketset.value.id, groupTicket.value.code, groupOnline.value)
        }
    }

    function onGroupRefuse() {
        tickets.setLastCode('')
    }

    function onManualSubmit(code: string) {
        processCode(code, true)
    }

    async function loadActiveTickets() {
        const ticketTicketsetIds = tickets.tickets.map(ts => String(ts.id))

        let selectedIds: number[] = []
        if (!events.activeZones.length) {
            selectedIds = events.activeTicketsetIds.map(Number)
        }

        if (events.activeZones.length) {
            const zonesKey = JSON.stringify(events.activeZones)
            const oldKey = events.lastZones
            const tooOld =
                !events.lastZoneLoadDate || new Date(events.lastZoneLoadDate).getTime() < Date.now() - 86_400_000
            if (oldKey !== zonesKey || tooOld) {
                tickets.clearTickets()
                try {
                    await events.loadZones()
                } catch {
                    // ignore
                }
                loading.value = true
                try {
                    await tickets.loadTicketsZones()
                } catch {
                    // ignore
                }
                events.setLastZones(zonesKey)
                events.setLastZoneLoadDate(new Date().toISOString())
                loading.value = false
            }
            tickets.updateLoopStart()
        } else {
            // Pojistka: pokud nemáme co načítat, ale máme cached tickety, prostě je použijeme.
            if (selectedIds.length === 0 && ticketTicketsetIds.length > 0) {
                tickets.updateLoopStart()
                return
            }
            const same = ticketTicketsetIds.sort().join(',') === selectedIds.map(String).sort().join(',')
            if (!same) {
                tickets.clearTickets()
                loading.value = true
                try {
                    await tickets.loadTickets(selectedIds)
                } catch {
                    // ignore
                }
                loading.value = false
            }
            tickets.updateLoopStart()
        }
    }

    onMounted(() => {
        if (!isAudioContextRunning()) soundDialogOpen.value = true

        messages.clearMessages()
        tickets.setLastCode('')
        scanSuppressedUntil = performance.now() + SCAN_SUPPRESS_MS

        if (settings.nfc && 'NDEFReader' in window) {
            startNfcReader({
                onCodes: codes => {
                    let processed = false
                    for (const code of codes) {
                        const [ticket, ticketset] = tickets.getTicketByCode(code)
                        if (ticket && ticketset) {
                            processCode(code)
                            processed = true
                            break
                        }
                    }
                    if (!processed) {
                        messages.addMessage({ type: 'not-found', message: t('scanner.nfcNotFound') })
                        if (settings.sound) playAlarm()
                    }
                },
                onError: err => console.warn('NFC', err)
            })
                .then(h => {
                    nfcHandle = h
                })
                .catch(err => console.warn('NFC start failed', err))
        }

        if (!props.test) {
            void loadActiveTickets()
        }

        window.addEventListener('keydown', onKey)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('keydown', onKey)
        if (!props.test) tickets.updateLoopStop()
        if (clearTicketTimer) window.clearTimeout(clearTicketTimer)
        nfcHandle?.stop()
        nfcHandle = null
    })
</script>
